import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AccountBalance,
  GeneralLedgerTransaction,
  Response,
  AccountType,
  ChangeLog,
  Pagination,
} from '@ui/src';
import { customersComponentApi } from '../../services/customers';

export interface CustomersGeneralTransactionState {
  isLoading: boolean;
  transaction?: GeneralLedgerTransaction;
  debitAccountBalance?: AccountBalance;
  creditAccountBalance?: AccountBalance;
  transactionId?: string;
  transactionChangeLogs?: ChangeLog[];
  transactionChangeLogsPagination?: Pagination;
}

const initialState: CustomersGeneralTransactionState = {
  isLoading: false,
  transaction: undefined,
  transactionId: undefined,
};

export const customersGeneralTransactionSlice = createSlice({
  name: 'customerGeneralTransaction',
  initialState,
  reducers: {
    clearTransactionState: (state) => {
      state.transaction = undefined;
      state.transactionId = undefined;
      state.transactionChangeLogs = undefined;
      state.transactionChangeLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        customersComponentApi.endpoints.getCustomerGeneralLedgerTransaction
          .matchFulfilled,
        (state, action: PayloadAction<{ data: GeneralLedgerTransaction }>) => {
          state.transaction = action.payload.data;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomerStatementChangeLogs
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<ChangeLog>> & {
            meta: { arg: { originalArgs: string } };
          },
        ) => {
          if (state.transaction) {
            if (!action.meta.arg.originalArgs.includes('cursor')) {
              state.transactionChangeLogs = action.payload.data?.edges?.map(
                (item) => item.node,
              );
            } else {
              const nextChangeLogs = ([] as ChangeLog[]).concat(
                state.transactionChangeLogs as ChangeLog[],
              );

              action.payload.data.edges?.forEach((item) => {
                nextChangeLogs.push(item.node);
              });

              state.transactionChangeLogs = nextChangeLogs;
            }

            state.transactionChangeLogsPagination =
              action.payload.data?.pageInfo;
          }
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.getCustomersAccountBalanceById
          .matchFulfilled,
        (
          state,
          action: PayloadAction<Response<AccountBalance>> & {
            meta: {
              arg: {
                originalArgs: {
                  id: string;
                  query?: string | undefined;
                  typeAcc: AccountType;
                };
              };
            };
          },
        ) => {
          if (action.meta.arg.originalArgs.typeAcc) {
            const [accBalance] = action.payload.data.edges.map(
              (item) => item.node,
            );

            if (action.meta.arg.originalArgs.typeAcc === AccountType.DEBIT) {
              state.debitAccountBalance = accBalance;
            } else if (
              action.meta.arg.originalArgs.typeAcc === AccountType.CREDIT
            ) {
              state.creditAccountBalance = accBalance;
            }
          }
        },
      );
  },
});

export const { clearTransactionState } =
  customersGeneralTransactionSlice.actions;

export default customersGeneralTransactionSlice.reducer;
